import React, { useMemo, useRef, useState } from "react";
import {
  FaTimes,
  FaFileExcel,
  FaUpload,
  FaChevronRight,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import * as XLSX from "xlsx";



const ImportEmployeesPopup = ({
  visible,
  onClose,
  onImport,
}) => {
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [isImporting, setIsImporting] = useState(false);


  const [selectedFile, setSelectedFile] =
    useState(null);

  const [parsedRows, setParsedRows] =
    useState([]);

  const [headers, setHeaders] =
    useState([]);

  const [columnMapping, setColumnMapping] =
    useState({
        name: "",
        customer: "",
        location: "",
        instructions: "",
    });

 



  const canContinueImport =
    !!columnMapping.name;

  const transformedPreviewRows =
    useMemo(() => {
        return parsedRows.map((row) => ({
        name:
            row[columnMapping.name] || "-",

        customer:
            row[columnMapping.customer] || "-",

        location:
            row[columnMapping.location] || "-",

        instructions:
            row[columnMapping.instructions] || "-",
        }));
    }, [
        parsedRows,
        columnMapping,
    ]);

  const importWarnings =
    useMemo(() => {
      const warnings = [];

      if (!columnMapping.name) {
        warnings.push(
          "Projektnamn måste vara mappat innan import."
        );
      }
      return warnings;
    }, [
      columnMapping,
    ]);

  if (!visible) return null;

  const spinner = (
    <div
      style={{
        width: 18,
        height: 18,
        border:
          "2px solid rgba(255,255,255,0.35)",
        borderTop:
          "2px solid white",
        borderRadius: "50%",
        animation:
          "spin 0.8s linear infinite",
      }}
    />
  );

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setSelectedFile(file);
  };

  const handleImport = () => {
    if (!selectedFile) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = evt.target.result;

      const workbook = XLSX.read(data, {
        type: "binary",
      });

      const sheetName =
        workbook.SheetNames[0];

      const worksheet =
        workbook.Sheets[sheetName];

      const jsonData =
        XLSX.utils.sheet_to_json(
          worksheet,
          {
            defval: "",
          }
        );

      if (!jsonData.length) return;

      setParsedRows(jsonData);

      setHeaders(
        Object.keys(jsonData[0])
      );

      setStep(2);
    };

    reader.readAsBinaryString(
      selectedFile
    );
  };

  const renderRequiredBadge = () => (
    <div
      style={{
        height: 24,
        padding: "0 10px",
        borderRadius: 999,
        background: "#FEE2E2",
        color: "#DC2626",
        fontSize: 11,
        fontWeight: 800,
        display: "flex",
        alignItems: "center",
        letterSpacing: 0.4,
        textTransform: "uppercase",
      }}
    >
      Krävs
    </div>
  );

  const renderMappingCard = (
    label,
    field,
    description,
    required = false
  ) => (
    <div
      style={{
        background: "white",
        border:
          required &&
          !columnMapping[field]
            ? "1px solid #FCA5A5"
            : "1px solid #E5E7EB",
        borderRadius: 18,
        padding: 20,
        transition:
          "all 120ms ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent:
            "space-between",
          gap: 20,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 8,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {label}
            </div>

            {required &&
              renderRequiredBadge()}
          </div>

          <div
            style={{
              fontSize: 13,
              color: "#6B7280",
              lineHeight: 1.7,
            }}
          >
            {description}
          </div>
        </div>

        <div
          style={{
            width: 280,
            flexShrink: 0,
          }}
        >
          <select
            value={columnMapping[field]}
            onChange={(e) =>
              setColumnMapping((prev) => ({
                ...prev,
                [field]:
                  e.target.value,
              }))
            }
            style={{
              width: "100%",
              height: 48,
              borderRadius: 14,
              border:
                required &&
                !columnMapping[field]
                  ? "1px solid #FCA5A5"
                  : "1px solid #D1D5DB",
              padding: "0 16px",
              fontSize: 14,
              background: "white",
              color: "#111827",
              outline: "none",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            <option value="">
              Importera inte
            </option>

            {headers.map(
              (header) => (
                <option
                  key={header}
                  value={header}
                >
                  {header}
                </option>
              )
            )}
          </select>
        </div>
      </div>
    </div>
  );

  return (

      <>
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }

            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    <div
      onClick={() => {
        if (isImporting) return;

        onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background:
          "rgba(15,23,42,0.55)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999999,
        padding: 24,
      }}
    >
      <div
        onClick={(e) =>
          e.stopPropagation()
        }
        style={{
          width: "100%",
          maxWidth: 1680,
          height: "92vh",
          background: "white",
          borderRadius: 32,
          overflow: "hidden",
          boxShadow:
            "0 30px 80px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            padding: "28px 36px",
            borderBottom:
              "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",
            background:
              "linear-gradient(to bottom, #ffffff, #FCFCFD)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 26,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Importera projekt
            </div>

            <div
              style={{
                marginTop: 10,
                fontSize: 14,
                color: "#6B7280",
                lineHeight: 1.7,
              }}
            >
              {step === 1
                ? "Importera projekt från ett tidigare system genom att ladda upp en Excel- eller CSV-fil."
                : "Matcha kolumner och verifiera all data innan projekt importeras till Timesmart."}
            </div>
          </div>

          <button     
            onClick={() => {
              if (isImporting) return;

              onClose();
            }}
            style={{
              width: 44,
              height: 44,
              borderRadius: 14,
              border:
                "1px solid #E5E7EB",
              background: "white",
              display: "flex",
              alignItems: "center",
              justifyContent:
                "center",
              cursor: "pointer",
              color: "#6B7280",
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <>
            <div
              style={{
                flex: 1,
                padding: 36,
              }}
            >
              <div
                onClick={() =>
                  fileInputRef.current?.click()
                }
                style={{
                  height: "100%",
                  border:
                    "2px dashed #CBD5E1",
                  borderRadius: 32,
                  background:
                    "linear-gradient(to bottom, #F8FAFC, #F1F5F9)",
                  display: "flex",
                  flexDirection:
                    "column",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                  textAlign:
                    "center",
                  cursor: "pointer",
                  padding: 32,
                }}
              >
                <div
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: 30,
                    background:
                      "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
                    display: "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    color: "white",
                    fontSize: 36,
                    marginBottom: 28,
                    boxShadow:
                      "0 20px 40px rgba(34,197,94,0.25)",
                  }}
                >
                  <FaFileExcel />
                </div>

                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color:
                      "#111827",
                    marginBottom: 12,
                  }}
                >
                  Välj importfil
                </div>

                <div
                  style={{
                    fontSize: 15,
                    color:
                      "#6B7280",
                    lineHeight: 1.9,
                    maxWidth: 620,
                  }}
                >
                  Timesmart analyserar filen innan något sparas.
                  <br />
                  Du får möjlighet att matcha kolumner, verifiera kunder och kontrollera datan innan importen slutförs.
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={
                    handleFileChange
                  }
                  style={{
                    display:
                      "none",
                  }}
                />

                {selectedFile && (
                  <div
                    style={{
                      marginTop: 34,
                      background:
                        "white",
                      border:
                        "1px solid #E5E7EB",
                      borderRadius: 20,
                      padding:
                        "18px 22px",
                      minWidth: 420,
                      display: "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "space-between",
                      boxShadow:
                        "0 10px 30px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color:
                            "#111827",
                        }}
                      >
                        {
                          selectedFile.name
                        }
                      </div>

                      <div
                        style={{
                          marginTop: 5,
                          fontSize: 12,
                          color:
                            "#6B7280",
                        }}
                      >
                        {(
                          selectedFile.size /
                          1024
                        ).toFixed(1)}{" "}
                        KB
                      </div>
                    </div>

                    <button
                      onClick={(
                        e
                      ) => {
                        e.stopPropagation();

                        setSelectedFile(
                          null
                        );
                      }}
                      style={{
                        border:
                          "none",
                        background:
                          "transparent",
                        color:
                          "#EF4444",
                        cursor:
                          "pointer",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      Ta bort
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* FOOTER */}
            <div
              style={{
                padding: "26px 36px",
                borderTop:
                  "1px solid #E5E7EB",
                display: "flex",
                justifyContent:
                  "space-between",
              }}
            >
              <button
                onClick={onClose}
                style={{
                  height: 50,
                  padding:
                    "0 22px",
                  borderRadius: 14,
                  border:
                    "1px solid #D1D5DB",
                  background:
                    "white",
                  fontWeight: 600,
                  cursor:
                    "pointer",
                  fontSize: 14,
                }}
              >
                Avbryt
              </button>

              <button
                onClick={
                  handleImport
                }
                disabled={
                  !selectedFile
                }
                style={{
                  height: 50,
                  padding:
                    "0 24px",
                  borderRadius: 14,
                  border: "none",
                  background:
                    selectedFile
                      ? "#2563EB"
                      : "#CBD5E1",
                  color: "white",
                  fontWeight: 700,
                  cursor:
                    selectedFile
                      ? "pointer"
                      : "not-allowed",
                  display: "flex",
                  alignItems:
                    "center",
                  gap: 10,
                  fontSize: 14,
                }}
              >
                Fortsätt
                <FaChevronRight
                  style={{
                    fontSize: 12,
                  }}
                />
              </button>
            </div>
          </>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <>
            <div
              style={{
                flex: 1,
                overflow: "hidden",
                display: "grid",
                gridTemplateColumns:
                  "640px minmax(0,1fr)",
              }}
            >
              {/* LEFT */}
              <div
                style={{
                  borderRight:
                    "1px solid #E5E7EB",
                  background:
                    "#FAFBFC",
                  overflowY:
                    "auto",
                  padding: 36,
                }}
              >
                {/* INTRO */}
                <div
                  style={{
                    marginBottom: 30,
                    background:
                      "white",
                    border:
                      "1px solid #E5E7EB",
                    borderRadius: 22,
                    padding: 24,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: 14,
                        background:
                          "#DBEAFE",
                        display: "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                        color:
                          "#2563EB",
                      }}
                    >
                      <FaCheckCircle />
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color:
                            "#111827",
                        }}
                      >
                        Matcha information
                      </div>

                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 13,
                          color:
                            "#6B7280",
                        }}
                      >
                        Kontrollera all data innan import.
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: 14,
                      lineHeight: 1.9,
                      color: "#6B7280",
                    }}
                  >
                    Fält markerade med
                    {" "}
                    {renderRequiredBadge()}
                    {" "}
                    måste vara korrekt mappade innan importen kan fortsätta.
                  </div>
                </div>

                {/* WARNINGS */}
                {importWarnings.length >
                  0 && (
                  <div
                    style={{
                      marginBottom: 28,
                      background:
                        "#FEF2F2",
                      border:
                        "1px solid #FECACA",
                      borderRadius: 20,
                      padding: 22,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 16,
                        color:
                          "#DC2626",
                        fontWeight: 700,
                        fontSize: 15,
                      }}
                    >
                      <FaExclamationTriangle />
                      Åtgärder krävs innan import
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection:
                          "column",
                        gap: 10,
                      }}
                    >
                      {importWarnings.map(
                        (
                          warning,
                          index
                        ) => (
                          <div
                            key={index}
                            style={{
                              fontSize: 13,
                              color:
                                "#7F1D1D",
                              lineHeight: 1.7,
                            }}
                          >
                            • {warning}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* FIELD MAPPING */}
                <div
                  style={{
                    display: "flex",
                    flexDirection:
                      "column",
                    gap: 18,
                  }}
                >
                    {renderMappingCard(
                    "Projektnamn",
                    "name",
                    "Projektets namn.",
                    true
                    )}

                    {renderMappingCard(
                    "Kund",
                    "customer",
                    "Kunden projektet tillhör."
                    )}

                    {renderMappingCard(
                    "Adress",
                    "location",
                    "Projektets adress."
                    )}

                    {renderMappingCard(
                    "Instruktioner",
                    "instructions",
                    "Projektinstruktioner."
                    )}

                </div>
              </div>

              {/* RIGHT */}
              <div
                style={{
                  overflow: "hidden",
                  display: "flex",
                  flexDirection:
                    "column",
                  padding: 36,
                  background:
                    "#FFFFFF",
                }}
              >
                <div
                  style={{
                    marginBottom: 24,
                  }}
                >
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color:
                        "#111827",
                      marginBottom: 12,
                    }}
                  >
                    Förhandsvisning av data
                  </div>

                  <div
                    style={{
                      fontSize: 14,
                      color: "#6B7280",
                      lineHeight: 1.8,
                      maxWidth: 760,
                    }}
                  >
                    Detta är informationen som kommer importeras till Timesmart efter att importen slutförts.
                    <br />
                    Enbart första 25 raderna visas i förhandsvisning.
                  </div>
                </div>

                <div
                  style={{
                    flex: 1,
                    border:
                      "1px solid #E5E7EB",
                    borderRadius: 24,
                    overflow:
                      "hidden",
                    background:
                      "white",
                    minHeight: 0,
                    boxShadow:
                      "0 10px 30px rgba(0,0,0,0.04)",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      overflow:
                        "auto",
                    }}
                  >
                    <table
                      style={{
                        width:
                          "100%",
                        borderCollapse:
                          "collapse",
                        minWidth: 1100,
                      }}
                    >
                      <thead
                        style={{
                          position:
                            "sticky",
                          top: 0,
                          zIndex: 2,
                          background:
                            "#F8FAFC",
                        }}
                      >
                        <tr>
                          {headers.map(
                            (
                              header
                            ) => (
                              <th
                                key={
                                  header
                                }
                                style={{
                                  padding:
                                    "18px 20px",
                                  textAlign:
                                    "left",
                                  fontSize: 13,
                                  fontWeight: 700,
                                  borderBottom:
                                    "1px solid #E5E7EB",
                                  color:
                                    "#374151",
                                  whiteSpace:
                                    "nowrap",
                                }}
                              >
                                {
                                  header
                                }
                              </th>
                            )
                          )}
                        </tr>
                      </thead>

                      <tbody>
                        {parsedRows
                          .slice(0, 25)
                          .map(
                            (
                              row,
                              index
                            ) => (
                              <tr
                                key={
                                  index
                                }
                              >
                                {headers.map(
                                  (
                                    header
                                  ) => (
                                    <td
                                      key={
                                        header
                                      }
                                      style={{
                                        padding:
                                          "16px 20px",
                                        borderBottom:
                                          "1px solid #F3F4F6",
                                        fontSize: 13,
                                        color:
                                          "#111827",
                                        whiteSpace:
                                          "nowrap",
                                      }}
                                    >
                                      {
                                        row[
                                          header
                                        ]
                                      }
                                    </td>
                                  )
                                )}
                              </tr>
                            )
                          )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div
              style={{
                padding: "26px 36px",
                borderTop:
                  "1px solid #E5E7EB",
                display: "flex",
                justifyContent:
                  "space-between",
                background:
                  "#FCFCFD",
              }}
            >
              <button
                onClick={() =>
                  setStep(1)
                }
                style={{
                  height: 50,
                  padding:
                    "0 22px",
                  borderRadius: 14,
                  border:
                    "1px solid #D1D5DB",
                  background:
                    "white",
                  fontWeight: 600,
                  cursor:
                    "pointer",
                  fontSize: 14,
                }}
              >
                Tillbaka
              </button>

              <button
                disabled={
                  !canContinueImport
                }
                onClick={() =>
                  setStep(3)
                }
                style={{
                  height: 50,
                  padding:
                    "0 24px",
                  borderRadius: 14,
                  border: "none",
                  background:
                    canContinueImport
                      ? "#2563EB"
                      : "#CBD5E1",
                  color: "white",
                  fontWeight: 700,
                  cursor:
                    canContinueImport
                      ? "pointer"
                      : "not-allowed",
                  fontSize: 14,
                  display: "flex",
                  alignItems:
                    "center",
                  gap: 10,
                  boxShadow:
                    canContinueImport
                      ? "0 10px 25px rgba(37,99,235,0.25)"
                      : "none",
                }}
              >
                Fortsätt
                <FaChevronRight
                  style={{
                    fontSize: 12,
                  }}
                />
              </button>
            </div>
          </>
        )}

              {/* STEP 3 */}
              {step === 3 && (
                <>
                  <div
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      padding: 36,
                      display: "flex",
                      flexDirection:
                        "column",
                      background:
                        "#F8FAFC",
                    }}
                  >
                    {/* TOP */}
                    <div
                      style={{
                        background: "white",
                        border:
                          "1px solid #E5E7EB",
                        borderRadius: 24,
                        padding: 28,
                        marginBottom: 28,
                        display: "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "space-between",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 700,
                            color: "#111827",
                            marginBottom: 10,
                          }}
                        >
                          Förhandsgranska import
                        </div>

                        <div
                          style={{
                            fontSize: 14,
                            color: "#6B7280",
                            lineHeight: 1.8,
                          }}
                        >
                        Detta är exakt hur
                        projekt kommer
                        importeras till
                        Timesmart.
                        </div>
                      </div>

                      <div
                        style={{
                          height: 52,
                          padding: "0 20px",
                          borderRadius: 16,
                          background:
                            "#DCFCE7",
                          color: "#15803D",
                          fontWeight: 700,
                          display: "flex",
                          alignItems:
                            "center",
                          gap: 10,
                          fontSize: 15,
                        }}
                      >
                        <FaCheckCircle />
                        {parsedRows.length} redo
                      </div>
                    </div>

                    {/* TABLE */}
                    <div
                      style={{
                        flex: 1,
                        background: "white",
                        border:
                          "1px solid #E5E7EB",
                        borderRadius: 28,
                        overflow: "hidden",
                        minHeight: 0,
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          overflow: "auto",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse:
                              "collapse",
                            minWidth: 1200,
                          }}
                        >
                          <thead
                            style={{
                              position:
                                "sticky",
                              top: 0,
                              zIndex: 5,
                              background:
                                "#F8FAFC",
                            }}
                          >
                            <tr>
                              {[
                                "Projektnamn",
                                "Kund",
                                "Adress",
                                "Instruktioner",
                                ].map(
                                (header) => (
                                  <th
                                    key={header}
                                    style={{
                                      padding:
                                        "18px 22px",
                                      textAlign:
                                        "left",
                                      fontSize: 13,
                                      fontWeight: 700,
                                      color:
                                        "#374151",
                                      borderBottom:
                                        "1px solid #E5E7EB",
                                      whiteSpace:
                                        "nowrap",
                                    }}
                                  >
                                    {header}
                                  </th>
                                )
                              )}
                            </tr>
                          </thead>

                          <tbody>
                            {transformedPreviewRows.map(
                              (
                                row,
                                index
                              ) => (
                                <tr
                                  key={index}
                                >
                                  <td
                                    style={{
                                      padding:
                                        "18px 22px",
                                      borderBottom:
                                        "1px solid #F3F4F6",
                                      fontWeight: 600,
                                      color:
                                        "#111827",
                                    }}
                                  >
                                    {row.name}
                                  </td>

                                  <td
                                    style={{
                                      padding:
                                        "18px 22px",
                                      borderBottom:
                                        "1px solid #F3F4F6",
                                      color:
                                        "#374151",
                                    }}
                                  >
                                    {row.customer}
                                  </td>

                                  <td
                                    style={{
                                      padding:
                                        "18px 22px",
                                      borderBottom:
                                        "1px solid #F3F4F6",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display:
                                          "inline-flex",
                                        alignItems:
                                          "center",
                                        height: 34,
                                        padding:
                                          "0 14px",
                                        borderRadius:
                                          999,
                                        background:
                                          "#EFF6FF",
                                        color:
                                          "#1D4ED8",
                                        fontSize: 13,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {row.location}
                                    </div>
                                  </td>

                                  <td
                                    style={{
                                      padding:
                                        "18px 22px",
                                      borderBottom:
                                        "1px solid #F3F4F6",
                                      color:
                                        "#374151",
                                    }}
                                  >
                                    {row.instructions}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* FOOTER */}
                  <div
                    style={{
                      padding: "26px 36px",
                      borderTop:
                        "1px solid #E5E7EB",
                      display: "flex",
                      justifyContent:
                        "space-between",
                      background:
                        "#FCFCFD",
                    }}
                  >
                    <button
                      onClick={() =>
                        setStep(2)
                      }
                      style={{
                        height: 50,
                        padding: "0 22px",
                        borderRadius: 14,
                        border:
                          "1px solid #D1D5DB",
                        background: "white",
                        fontWeight: 600,
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                    >
                      Tillbaka
                    </button>

                    <button
                      onClick={async () => {
                        try {
                          setIsImporting(true);

                        await onImport?.({
                         parsedRows,
                         columnMapping,
                        });
                        } finally {
                          setIsImporting(false);
                        }
                      }}
                      style={{
                        height: 50,
                        padding: "0 24px",
                        borderRadius: 14,
                        border: "none",
                        background:
                          "#16A34A",
                        color: "white",
                        fontWeight: 700,
                        cursor: isImporting
                        ? "not-allowed"
                        : "pointer",

                        opacity: isImporting ? 0.7 : 1,

                        fontSize: 14,
                        display: "flex",
                        alignItems:
                          "center",
                        gap: 10,
                        boxShadow:
                          "0 10px 25px rgba(22,163,74,0.25)",
                      }}
                    >
                    {isImporting ? (
                      <>
                        {spinner}
                        Importerar...
                      </>
                    ) : (
                      <>
                        <FaUpload />
                        Importera projekt
                      </>
                    )}
                    </button>
                  </div>
                </>
              )}

      </div>
    </div>
     </>
  );
};

export default ImportEmployeesPopup;